import { useEffect, useState } from "react";
import { api, money, dateIT } from "@/lib/api";
import { PageHeader, Btn, Field, Select, Badge } from "@/components/UI";
import { ShieldCheck, WarningCircle, XCircle, CheckCircle, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";

export default function Compliance() {
  const [filiere, setFiliere] = useState([]);
  const [sel, setSel] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/filiere").then((r) => setFiliere(r.data)); }, []);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/check", { params: sel ? { filiera_id: sel } : {} });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [sel]);

  const scoreColor = (s) => s === 100 ? "text-emerald-600" : s >= 70 ? "text-amber-600" : "text-red-600";
  const statusBadge = (s) => s === "OK" ? "green" : s === "WARN" ? "amber" : "red";

  return (
    <div className="fade-in" data-testid="compliance-page">
      <PageHeader eyebrow="Controlli" title="Controllo di Conformità"
        actions={
          <>
            <Select value={sel} onChange={(e) => setSel(e.target.value)} className="!w-56" data-testid="compliance-filiera">
              <option value="">Tutte le filiere</option>
              {filiere.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </Select>
            <Btn variant="secondary" onClick={run} data-testid="compliance-refresh">
              <ArrowsClockwise size={16} weight="bold" /> Ricalcola
            </Btn>
          </>
        } />

      <div className="p-8 space-y-6">
        {loading && <div className="text-slate-500">Analisi in corso…</div>}
        {data && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="stat-card lg:col-span-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="label">Score globale conformità</div>
                    <div className={`value ${scoreColor(data.summary.global_score)}`}>{data.summary.global_score}%</div>
                    <div className="delta">
                      {data.summary.ready_for_sal ?
                        <span className="text-emerald-600 flex items-center gap-1"><Sparkle size={14} weight="fill"/> Pronto per SAL</span> :
                        <span className="text-amber-600">Necessarie correzioni prima del SAL</span>}
                    </div>
                  </div>
                  <div className="w-16 h-16 rounded-full border-4 border-slate-100 flex items-center justify-center">
                    <ShieldCheck size={32} weight="duotone" className={scoreColor(data.summary.global_score)} />
                  </div>
                </div>
              </div>
              <div className="stat-card"><div className="label">Fatture totali</div><div className="value">{data.summary.total}</div></div>
              <div className="stat-card"><div className="label">Conformi</div><div className="value text-emerald-600">{data.summary.ok}</div></div>
              <div className="stat-card"><div className="label">Anomalie</div><div className="value text-red-600">{data.summary.ko + data.summary.warn}</div></div>
            </div>

            {/* Checklist */}
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="eyebrow">Checklist "Pronto per il SAL"</div>
                  <div className="text-xs text-slate-500 mt-1">Verifica per fattura di: CUP, dicitura PNRR, fornitore, investimento, bonifico, data, totale</div>
                </div>
              </div>
              {data.checks.length === 0 ? (
                <div className="p-10 text-center text-slate-500 text-sm">Nessuna fattura registrata</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Stato</th>
                      <th>N. fattura</th>
                      <th>Data</th>
                      <th>Fornitore</th>
                      <th className="text-right">Totale</th>
                      <th>Score</th>
                      <th>Controlli</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.checks.map((c) => (
                      <tr key={c.documento_id} data-testid={`check-row-${c.documento_id}`}>
                        <td><Badge tone={statusBadge(c.status)}>{c.status}</Badge></td>
                        <td className="font-medium mono">{c.numero || "-"}</td>
                        <td>{dateIT(c.data)}</td>
                        <td>{c.fornitore || "-"}</td>
                        <td className="text-right font-medium">{money(c.totale)}</td>
                        <td className={`font-bold ${scoreColor(c.score)}`}>{c.score}%</td>
                        <td>
                          <div className="flex flex-wrap gap-1.5">
                            {c.rules.map((r, i) => (
                              <span key={i} title={r.rule} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${r.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                                {r.ok ? <CheckCircle size={11} weight="fill" /> : <XCircle size={11} weight="fill" />}
                                {r.rule}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
